import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceLocator } from '@base/constants/service-locator';
import { ProfileData } from '@profile/model/profile.model';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class BaseComponent {
  private static _token: string;
  private static _isLoggedIn: boolean = false;
  private static _user: ProfileData = null;
  private static _userId: string = null;

  public router: Router;
  public toastr: ToastrService;
  public imagePrefix = environment.imagePrefix;
  public certificationDictionary = {
		"G": {"code": "ATP", "description": "Apta para todo público"},
		"PG": {"code": "7+", "description": "Se recomienda supervisión de los padres."},
		"PG-7": {"code": "7+", "description": "Apto mayores de 7 años con supervisión de los padres."},
		"PG-13": {"code": "13+", "description": "Apto mayores de 13 años con supervisión de los padres."},
		"PG-13 R": {"code": "13+", "description": "Apto mayores de 13 años con supervisión obligatoria de los padres."},
		"R": {"code": "16+", "description": "Apto mayores de 16 años"},
		"18+": {"code": "18+", "description": "Solo apto mayores de 18 años"},
		"30+": {"code": "18+", "description": "Solo apto mayores de 18 años"},
	};

  constructor() {
    this.router = ServiceLocator.injector.get(Router);
    this.toastr = ServiceLocator.injector.get(ToastrService);
  }

  public static get token(): string {
    return BaseComponent._token;
  }

  public get token(): string {
    return BaseComponent._token;
  }

  public get isLoggedIn(): boolean {
    return BaseComponent._isLoggedIn;
  }

  public get userId(): string {
    return BaseComponent._userId;
  }

  public get user(): ProfileData {
    return BaseComponent._user;
  }

  // LocalStorage Actions
  public static fillAuthData() {
    const userToken = localStorage.getItem('user_token');
    const guestToken = localStorage.getItem('guest_token');
    
    BaseComponent._userId = localStorage.getItem('user_id');
    BaseComponent._token = userToken || guestToken;
    BaseComponent._isLoggedIn = !!userToken;

    return {
      isLoggedIn: BaseComponent._isLoggedIn,
      token: userToken || guestToken
    };
  }

  public static clearAuthData() {
    // excecuted when is signing out
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_token');
    localStorage.removeItem('guest_token');

    BaseComponent._userId = null
    BaseComponent._token = null;
    BaseComponent._isLoggedIn = false;
  }

  public static setGuestToken(token: string) {
    token = `Bearer ${token}`;
    localStorage.setItem('guest_token', token);
    BaseComponent._token = token;
  }

  public static setUserToken(token: string) {
    token = `Bearer ${token}`;
    localStorage.setItem('user_token', token);
    BaseComponent._token = token;
    BaseComponent._isLoggedIn = true;
  }

  public static setUser(user) {
    localStorage.setItem('user_id', user.id);
    BaseComponent._user = { ...user } as ProfileData;
  }

  public waitForToken() {
    return new Promise((res, rej) => {
      let waitTillGuestTokenInterval = null
      waitTillGuestTokenInterval = setInterval(() => {
        if (BaseComponent._token) {
          clearInterval(waitTillGuestTokenInterval);
          res(0)
        }
      }, 500);
    })
  }

  public isWindowWidthLTE(dimension): boolean {
    return window.innerWidth <= dimension;
  }
  
  public setTenantBucket(path: string): string {
		if (!path) return path;
		if (path.includes("https://oneplay-content")) {
			return path.replace("https://oneplay-content", "https://oneplay-content");
		} else if (path.includes("https://oneplaycontent.dcafecms.com/")){
      return path.replace("https://oneplaycontent", "https://oneplaycontent");
    }
		return path;
	}
  public hmsToSecondsOnly(str: string): number {
		let p = str.split(':'),
			s = 0, m = 1;

		if (p.length < 3)
			p = `${str}:00`.split(':')

		while (p.length > 0) {
			s += m * parseInt(p.pop(), 10);
			m *= 60;
		}

		return s;
	}

  public secondsToRoundedMinutes(seconds: number): number {
    return Math.round(seconds / 60);
  }

  public getBookmarkWidth(bookmarkDuration, duration) {
    if (bookmarkDuration && !duration) {
        duration = 7200;
    } else if (bookmarkDuration && duration) {
        if (typeof duration === 'string') {
            duration = this.hmsToSecondsOnly(duration);
        }
        const percentage = ((bookmarkDuration * 100) / duration);
        if (percentage > 100) {
            return "100%";
        }
        return percentage.toString() + "%";
    }
    return "0%"; 
	}

  public getRemainingTime(bookmarkDuration, duration) {
    if (bookmarkDuration && !duration) {
        duration = 7200;
    } else if (bookmarkDuration && duration) {
        if (typeof duration === 'string') {
            duration = this.hmsToSecondsOnly(duration);
        }
        const remainingTime = duration - bookmarkDuration;
        if (remainingTime < 0)
            return null;
        return this.secondsToRoundedMinutes(remainingTime);
    }
    return null;
  }
  public decodeHtmlEntities(str) {
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = str;
		return tempDiv.textContent || tempDiv.innerText || "";
	}
  // Toast Functions
  public successResponse(message?: string) {
    this.toastr.success(message, '');
  }

  public errMessageResponse(message?: string) {
    this.toastr.error(message, '');
  }

  public warningMessageResponse(message?: string) {
    this.toastr.warning(message, '');
  }
  // Function to debug
  public printDebug(value: any) {
    console.log(value);
    return '';
  }
}
